import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  buscarConferencia, resolverDivergencia,
  adicionarItem, adicionarDocumento, criarDivergencia,
  atualizarDocumento, deletarDocumento, listarLeituras,
  analisarDivergenciasVolumes
} from '../../../services/conferencia';
import type { AnaliseVolumePedido, ResumoAnaliseVolumes } from '../../../services/conferencia';
import type { Conferencia, ConferenciaItem, ConferenciaDocumento, ConferenciaDivergencia, ConferenciaLeitura, ResumoLeituras, TipoDocumento } from '../../../types/conferencia';
import '../Conferencia.css';

const ConferenciaView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [conferencia, setConferencia] = useState<Conferencia | null>(null);
  const [itens, setItens] = useState<ConferenciaItem[]>([]);
  const [documentos, setDocumentos] = useState<ConferenciaDocumento[]>([]);
  const [divergencias, setDivergencias] = useState<ConferenciaDivergencia[]>([]);
  const [resumoLeituras, setResumoLeituras] = useState<ResumoLeituras | null>(null);
  const [activeTab, setActiveTab] = useState<'itens' | 'documentos' | 'leituras' | 'divergencias' | 'volumes'>('itens');

  const [showItemModal, setShowItemModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showDivModal, setShowDivModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const [itemForm, setItemForm] = useState({ codigo_barras: '', descricao: '', quantidade_esperada: 1, unidade_medida: 'UN', sku: '' });
  const [editingDocId, setEditingDocId] = useState<number | null>(null);
  const [docForm, setDocForm] = useState({ tipo_documento: 'nota_fiscal', numero_documento: '', serie: '', emitente: '', destinatario: '', valor_total: '', peso_total: '', total_volumes: 1 });
  const [divForm, setDivForm] = useState({ tipo_divergencia: 'falta', codigo_barras: '', descricao: '', quantidade_esperada: '', quantidade_encontrada: '' });
  const [leiturasLista, setLeiturasLista] = useState<ConferenciaLeitura[]>([]);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<{ id: number; status: 'resolvida' | 'aceita' } | null>(null);
  const [resolveText, setResolveText] = useState('');
  const [volumeAnalise, setVolumeAnalise] = useState<AnaliseVolumePedido[]>([]);
  const [volumeResumo, setVolumeResumo] = useState<ResumoAnaliseVolumes | null>(null);
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [volumeError, setVolumeError] = useState('');
  const [pedidoExpandido, setPedidoExpandido] = useState<string | null>(null);

  const carregar = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const result = await buscarConferencia(parseInt(id));
      if (result.success) {
        setConferencia(result.conferencia);
        setItens(result.itens);
        setDocumentos(result.documentos || []);
        setDivergencias(result.divergencias);
        setResumoLeituras(result.resumo_leituras);
      }
    } catch (error) {
      console.error('Erro ao carregar conferência:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const carregarVolumes = () => {
    if (!id) return;
    setVolumeLoading(true);
    setVolumeError('');
    analisarDivergenciasVolumes(parseInt(id))
      .then(result => {
        if (result.success) {
          setVolumeAnalise(result.analise_volumes);
          setVolumeResumo(result.resumo);
        }
      })
      .catch(err => {
        setVolumeError(err.message || 'Erro ao analisar volumes');
      })
      .finally(() => setVolumeLoading(false));
  };

  useEffect(() => {
    if (activeTab === 'leituras' && id) {
      listarLeituras(parseInt(id), { limit: 100 })
        .then(result => { if (result.success) setLeiturasLista(result.leituras); })
        .catch(err => console.error('Erro ao carregar leituras:', err));
    }
    if (activeTab === 'volumes') {
      carregarVolumes();
    }
  }, [activeTab, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdicionarItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setModalLoading(true);
    setModalError('');
    try {
      const result = await adicionarItem(parseInt(id), {
        codigo_barras: itemForm.codigo_barras || undefined,
        sku: itemForm.sku || undefined,
        descricao: itemForm.descricao || undefined,
        quantidade_esperada: itemForm.quantidade_esperada,
        unidade_medida: itemForm.unidade_medida
      });
      if (result.success) {
        setItens(prev => [...prev, result.item]);
        setShowItemModal(false);
        setItemForm({ codigo_barras: '', descricao: '', quantidade_esperada: 1, unidade_medida: 'UN', sku: '' });
      }
    } catch (err: any) {
      setModalError(err.message || 'Erro ao adicionar item');
    } finally {
      setModalLoading(false);
    }
  };

  const handleSalvarDocumento = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setModalLoading(true);
    setModalError('');
    try {
      const payload = {
        tipo_documento: docForm.tipo_documento as TipoDocumento,
        numero_documento: docForm.numero_documento,
        serie: docForm.serie || undefined,
        emitente: docForm.emitente || undefined,
        destinatario: docForm.destinatario || undefined,
        valor_total: docForm.valor_total ? parseFloat(docForm.valor_total) : undefined,
        peso_total: docForm.peso_total ? parseFloat(docForm.peso_total) : undefined,
        total_volumes: docForm.total_volumes
      };
      if (editingDocId) {
        const result = await atualizarDocumento(editingDocId, payload);
        if (result.success) setDocumentos(prev => prev.map(d => d.id === editingDocId ? result.documento : d));
      } else {
        const result = await adicionarDocumento(parseInt(id), payload);
        if (result.success) setDocumentos(prev => [...prev, result.documento]);
      }
      setShowDocModal(false);
      setEditingDocId(null);
      setDocForm({ tipo_documento: 'nota_fiscal', numero_documento: '', serie: '', emitente: '', destinatario: '', valor_total: '', peso_total: '', total_volumes: 1 });
    } catch (err: any) {
      setModalError(err.message || 'Erro ao salvar documento');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditarDocumento = (doc: ConferenciaDocumento) => {
    setEditingDocId(doc.id);
    setDocForm({
      tipo_documento: doc.tipo_documento, numero_documento: doc.numero_documento,
      serie: doc.serie || '', emitente: doc.emitente || '', destinatario: doc.destinatario || '',
      valor_total: doc.valor_total ? String(doc.valor_total) : '',
      peso_total: doc.peso_total ? String(doc.peso_total) : '',
      total_volumes: doc.total_volumes
    });
    setModalError('');
    setShowDocModal(true);
  };

  const handleDeletarDocumento = async (docId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;
    try {
      await deletarDocumento(docId);
      setDocumentos(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      console.error('Erro ao excluir documento:', err);
    }
  };

  const handleCriarDivergencia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setModalLoading(true);
    setModalError('');
    try {
      const result = await criarDivergencia(parseInt(id), {
        tipo_divergencia: divForm.tipo_divergencia,
        codigo_barras: divForm.codigo_barras || undefined,
        descricao: divForm.descricao || undefined,
        quantidade_esperada: divForm.quantidade_esperada ? parseInt(divForm.quantidade_esperada) : undefined,
        quantidade_encontrada: divForm.quantidade_encontrada ? parseInt(divForm.quantidade_encontrada) : undefined
      });
      if (result.success) {
        setDivergencias(prev => [...prev, result.divergencia]);
        setShowDivModal(false);
        setDivForm({ tipo_divergencia: 'falta', codigo_barras: '', descricao: '', quantidade_esperada: '', quantidade_encontrada: '' });
      }
    } catch (err: any) {
      setModalError(err.message || 'Erro ao criar divergência');
    } finally {
      setModalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (!conferencia) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
          <p>Conferência não encontrada</p>
        </div>
      </div>
    );
  }

  const progresso = conferencia.total_itens_esperado > 0
    ? Math.round((conferencia.total_itens_conferido / conferencia.total_itens_esperado) * 100)
    : 0;

  const getStatusClass = (status: string): string => {
    const map: Record<string, string> = {
      conferido: 'status-completed', pendente: 'status-pending', divergente: 'status-delayed',
      divergente_falta: 'status-delayed', divergente_sobra: 'status-delayed', divergente_avaria: 'status-delayed',
      parcial: 'status-maintenance', aberta: 'status-pending', resolvida: 'status-completed',
      aceita: 'status-active'
    };
    return map[status] || 'status-inactive';
  };

  const getLeituraIcon = (resultado: string): string => {
    const map: Record<string, string> = {
      ok: 'fas fa-check-circle', duplicado: 'fas fa-copy',
      nao_encontrado: 'fas fa-times-circle', avaria: 'fas fa-exclamation-triangle', erro: 'fas fa-times-circle',
      removido: 'fas fa-trash'
    };
    return map[resultado] || 'fas fa-question-circle';
  };

  const getLeituraColor = (resultado: string): string => {
    const map: Record<string, string> = {
      ok: '#27ae60', duplicado: '#f39c12', nao_encontrado: '#e74c3c', avaria: '#e67e22', erro: '#e74c3c',
      removido: '#95a5a6'
    };
    return map[resultado] || '#7f8c8d';
  };

  const getLeituraLabel = (resultado: string): string => {
    const map: Record<string, string> = {
      ok: 'OK', duplicado: 'Duplicado', nao_encontrado: 'Não Encontrado', avaria: 'Avaria', erro: 'Erro',
      removido: 'Removido'
    };
    return map[resultado] || resultado;
  };

  return (
    <div className="page-container">
      <button className="conferencia-back-link" onClick={() => navigate('/conferencia')}>
        <i className="fas fa-arrow-left"></i> Voltar para lista
      </button>

      {/* Header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h1 style={{ fontSize: '1.5rem', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-box-open"></i> Conferência #{conferencia.id}
              </h1>
              <div className="conferencia-info">
                <span>
                  {conferencia.tipo === 'pre_carregamento' && <i className="fas fa-box-open tipo-pre-carregamento"></i>}
                  {conferencia.tipo === 'carregamento' && <i className="fas fa-arrow-up tipo-carregamento"></i>}
                  {conferencia.tipo === 'descarregamento' && <i className="fas fa-arrow-down tipo-descarregamento"></i>}
                  {conferencia.tipo === 'pre_carregamento' ? 'Pré-Carregamento' : conferencia.tipo === 'carregamento' ? 'Carregamento' : 'Descarregamento'}
                </span>
                {conferencia.placa_veiculo && (
                  <span><i className="fas fa-truck"></i> {conferencia.placa_veiculo}</span>
                )}
                {conferencia.cliente_razao_social && (
                  <span><i className="fas fa-building"></i> {conferencia.cliente_razao_social}</span>
                )}
                <span><i className="fas fa-clock"></i> {new Date(conferencia.criado_em).toLocaleString('pt-BR')}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => navigate(`/conferencia/${id}/editar`)}>
                <i className="fas fa-edit"></i> Editar
              </button>
              <button className="btn btn-primary" onClick={() => navigate(`/conferencia/${id}/scanner`)}>
                <i className="fas fa-barcode"></i> Iniciar Leitura
              </button>
            </div>
          </div>

          {/* Resumo */}
          <div className="conferencia-resumo-grid">
            <div className="conferencia-resumo-card blue">
              <p className="resumo-label" style={{ color: '#1565c0' }}>Progresso</p>
              <p className="resumo-value" style={{ color: '#0d47a1' }}>{progresso}%</p>
              <div className="conferencia-progress" style={{ marginTop: 8 }}>
                <div className="progress-bar">
                  <div className={`progress-fill ${progresso >= 100 ? 'complete' : ''}`} style={{ width: `${progresso}%` }} />
                </div>
              </div>
            </div>
            <div className="conferencia-resumo-card green">
              <p className="resumo-label" style={{ color: '#2e7d32' }}>Itens Conferidos</p>
              <p className="resumo-value" style={{ color: '#1b5e20' }}>{conferencia.total_itens_conferido}/{conferencia.total_itens_esperado}</p>
            </div>
            <div className="conferencia-resumo-card red">
              <p className="resumo-label" style={{ color: '#c62828' }}>Divergências</p>
              <p className="resumo-value" style={{ color: '#b71c1c' }}>{conferencia.total_divergencias}</p>
            </div>
            <div className="conferencia-resumo-card purple">
              <p className="resumo-label" style={{ color: '#6a1b9a' }}>Total Leituras</p>
              <p className="resumo-value" style={{ color: '#4a148c' }}>{resumoLeituras?.total_leituras || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="conferencia-tab-header">
          <div className="conferencia-tabs">
            <button className={`conferencia-tab ${activeTab === 'itens' ? 'active' : ''}`} onClick={() => setActiveTab('itens')}>Itens ({itens.length})</button>
            <button className={`conferencia-tab ${activeTab === 'documentos' ? 'active' : ''}`} onClick={() => setActiveTab('documentos')}>Documentos ({documentos.length})</button>
            <button className={`conferencia-tab ${activeTab === 'leituras' ? 'active' : ''}`} onClick={() => setActiveTab('leituras')}>Leituras ({resumoLeituras?.total_leituras || 0})</button>
            <button className={`conferencia-tab ${activeTab === 'divergencias' ? 'active' : ''}`} onClick={() => setActiveTab('divergencias')}>Divergências ({divergencias.length})</button>
            <button className={`conferencia-tab ${activeTab === 'volumes' ? 'active' : ''}`} onClick={() => setActiveTab('volumes')}>
              <i className="fas fa-box" style={{ marginRight: 4 }}></i>Volumes
            </button>
          </div>
          <div style={{ padding: '8px 16px' }}>
            {activeTab === 'itens' && (
              <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.85rem' }} onClick={() => { setShowItemModal(true); setModalError(''); }}>
                <i className="fas fa-plus"></i> Adicionar Item
              </button>
            )}
            {activeTab === 'documentos' && (
              <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '0.85rem' }} onClick={() => { setEditingDocId(null); setDocForm({ tipo_documento: 'nota_fiscal', numero_documento: '', serie: '', emitente: '', destinatario: '', valor_total: '', peso_total: '', total_volumes: 1 }); setShowDocModal(true); setModalError(''); }}>
                <i className="fas fa-plus"></i> Adicionar Documento
              </button>
            )}
            {activeTab === 'divergencias' && (
              <button className="btn btn-danger" style={{ padding: '6px 12px', fontSize: '0.85rem' }} onClick={() => { setShowDivModal(true); setModalError(''); }}>
                <i className="fas fa-plus"></i> Registrar Divergência
              </button>
            )}
          </div>
        </div>

        <div className="card-body">
          {/* Tab: Itens */}
          {activeTab === 'itens' && (
            itens.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>Nenhum item cadastrado</div>
            ) : (
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Descrição</th>
                      <th>Esperado</th>
                      <th>Conferido</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map(item => (
                      <tr key={item.id}>
                        <td style={{ fontFamily: 'monospace' }}>{item.codigo_barras || item.sku || '-'}</td>
                        <td>{item.descricao || '-'}</td>
                        <td>{item.quantidade_esperada} {item.unidade_medida}</td>
                        <td style={{ fontWeight: 500 }}>{item.quantidade_conferida}</td>
                        <td><span className={`status-badge ${getStatusClass(item.status)}`}>{item.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Tab: Documentos */}
          {activeTab === 'documentos' && (
            documentos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>Nenhum documento vinculado</div>
            ) : (
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Número</th>
                      <th>Emitente</th>
                      <th>Valor</th>
                      <th>Volumes</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentos.map(doc => (
                      <tr key={doc.id}>
                        <td><i className="fas fa-file-alt" style={{ color: '#7f8c8d', marginRight: 6 }}></i>{doc.tipo_documento}</td>
                        <td style={{ fontFamily: 'monospace' }}>{doc.numero_documento}{doc.serie ? `/${doc.serie}` : ''}</td>
                        <td>{doc.emitente || '-'}</td>
                        <td>{doc.valor_total ? `R$ ${Number(doc.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}</td>
                        <td>{doc.volumes_conferidos}/{doc.total_volumes}</td>
                        <td><span className={`status-badge ${getStatusClass(doc.status)}`}>{doc.status}</span></td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn-edit" onClick={() => handleEditarDocumento(doc)} title="Editar"><i className="fas fa-edit"></i></button>
                            <button className="btn-delete" onClick={() => handleDeletarDocumento(doc.id)} title="Excluir"><i className="fas fa-trash"></i></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Tab: Divergências */}
          {activeTab === 'divergencias' && (
            divergencias.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>Nenhuma divergência encontrada</div>
            ) : (
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Esperado</th>
                      <th>Encontrado</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {divergencias.map(div => (
                      <tr key={div.id}>
                        <td><span className="status-badge status-delayed">{div.tipo_divergencia}</span></td>
                        <td>
                          {div.descricao || '-'}
                          {div.resolucao && <p style={{ fontSize: '0.75rem', color: '#7f8c8d', marginTop: 4, fontStyle: 'italic' }}>Resolução: {div.resolucao}</p>}
                        </td>
                        <td>{div.quantidade_esperada ?? '-'}</td>
                        <td>{div.quantidade_encontrada ?? '-'}</td>
                        <td><span className={`status-badge ${getStatusClass(div.status)}`}>{div.status}</span></td>
                        <td>
                          {div.status === 'aberta' && (
                            <div className="action-buttons">
                              <button className="btn-view" onClick={() => { setResolveTarget({ id: div.id, status: 'resolvida' }); setResolveText(''); setShowResolveModal(true); }}>Resolver</button>
                              <button className="btn-edit" onClick={() => { setResolveTarget({ id: div.id, status: 'aceita' }); setResolveText(''); setShowResolveModal(true); }}>Aceitar</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Tab: Leituras */}
          {activeTab === 'leituras' && (
            <div>
              {resumoLeituras && (
                <div className="conferencia-stats-grid" style={{ marginBottom: 20 }}>
                  <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                    <p className="stat-value">{resumoLeituras.total_leituras}</p>
                    <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Total</span>
                  </div>
                  <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                    <p className="stat-value" style={{ color: '#27ae60' }}>{resumoLeituras.leituras_ok}</p>
                    <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>OK</span>
                  </div>
                  <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                    <p className="stat-value" style={{ color: '#f39c12' }}>{resumoLeituras.duplicados}</p>
                    <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Duplicados</span>
                  </div>
                  <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                    <p className="stat-value" style={{ color: '#e74c3c' }}>{resumoLeituras.nao_encontrados}</p>
                    <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Não Encontrados</span>
                  </div>
                  <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                    <p className="stat-value" style={{ color: '#e67e22' }}>{resumoLeituras.avarias}</p>
                    <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Avarias</span>
                  </div>
                  {resumoLeituras.removidos > 0 && (
                    <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                      <p className="stat-value" style={{ color: '#95a5a6' }}>{resumoLeituras.removidos}</p>
                      <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Removidos</span>
                    </div>
                  )}
                </div>
              )}

              {leiturasLista.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>Nenhuma leitura realizada</div>
              ) : (
                <div style={{ maxHeight: 384, overflowY: 'auto' }}>
                  {leiturasLista.map(leitura => (
                    <div key={leitura.id} className="leitura-item" style={{ borderBottom: '1px solid #ecf0f1' }}>
                      <div className="leitura-info">
                        <i className={getLeituraIcon(leitura.resultado)} style={{ color: getLeituraColor(leitura.resultado) }}></i>
                        <div>
                          <span className="leitura-code">{leitura.codigo_lido}</span>
                          <p className="leitura-time">
                            {new Date(leitura.lido_em).toLocaleString('pt-BR')}
                            {leitura.usuario_nome && ` - ${leitura.usuario_nome}`}
                          </p>
                        </div>
                      </div>
                      <span className={`status-badge ${getStatusClass(leitura.resultado === 'ok' ? 'conferido' : leitura.resultado === 'duplicado' ? 'pendente' : 'divergente')}`}>
                        {getLeituraLabel(leitura.resultado)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab: Volumes */}
          {activeTab === 'volumes' && (
            <div>
              {/* Botão atualizar */}
              {!volumeLoading && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }} onClick={carregarVolumes}>
                    <i className="fas fa-sync"></i> Atualizar
                  </button>
                </div>
              )}

              {volumeLoading && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>
                  <span className="btn-spinner" style={{ marginRight: 8 }}></span>
                  Analisando volumes...
                </div>
              )}

              {volumeError && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>
                  {volumeError}
                </div>
              )}

              {!volumeLoading && !volumeError && volumeResumo && (
                <>
                  {/* Resumo de Volumes */}
                  <div className="conferencia-stats-grid" style={{ marginBottom: 20 }}>
                    <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                      <p className="stat-value">{volumeResumo.total_pedidos}</p>
                      <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Pedidos</span>
                    </div>
                    <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                      <p className="stat-value" style={{ color: '#27ae60' }}>{volumeResumo.pedidos_completos}</p>
                      <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Completos</span>
                    </div>
                    <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                      <p className="stat-value" style={{ color: '#e74c3c' }}>{volumeResumo.pedidos_incompletos}</p>
                      <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Incompletos</span>
                    </div>
                    <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                      <p className="stat-value">{volumeResumo.total_volumes_conferidos}/{volumeResumo.total_volumes_esperados}</p>
                      <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Volumes Conferidos</span>
                    </div>
                    <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
                      <p className="stat-value" style={{ color: volumeResumo.total_volumes_faltantes > 0 ? '#e74c3c' : '#27ae60' }}>
                        {volumeResumo.total_volumes_faltantes}
                      </p>
                      <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Faltantes</span>
                    </div>
                  </div>

                  {/* Lista de Pedidos */}
                  {volumeAnalise.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>
                      Nenhum volume identificado nas leituras desta conferência.
                    </div>
                  ) : (
                    <div className="conf-volumes-list">
                      {volumeAnalise.map((pedido) => {
                        const isExpanded = pedidoExpandido === pedido.numero_pedido;
                        return (
                        <div
                          key={pedido.numero_pedido}
                          className={`conf-pedido-card ${pedido.status}`}
                        >
                          <div
                            className="conf-pedido-header"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setPedidoExpandido(isExpanded ? null : pedido.numero_pedido)}
                          >
                            <div className="conf-pedido-info">
                              {pedido.status === 'completo'
                                ? <i className="fas fa-check-circle" style={{ color: '#27ae60', fontSize: '1.2rem' }}></i>
                                : <i className="fas fa-exclamation-triangle" style={{ color: pedido.status === 'incompleto' ? '#f39c12' : '#7f8c8d', fontSize: '1.2rem' }}></i>}
                              <div>
                                <strong className="conf-pedido-title">Pedido #{pedido.numero_pedido}</strong>
                                <p className="conf-pedido-subtitle">
                                  {pedido.quantidade_conferida} de {pedido.total_volumes} volumes conferidos
                                  {pedido.leituras.length > 0 && (
                                    <span style={{ marginLeft: 8, color: '#6b7280', fontSize: '0.75rem' }}>
                                      ({pedido.leituras.length} {pedido.leituras.length === 1 ? 'etiqueta bipada' : 'etiquetas bipadas'})
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className={`conf-pedido-status-badge ${pedido.status}`}>
                                {pedido.status === 'completo' ? 'Completo' : pedido.status === 'incompleto' ? 'Incompleto' : 'Pendente'}
                              </span>
                              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#9ca3af', fontSize: '0.8rem' }}></i>
                            </div>
                          </div>

                          {/* Barra de progresso */}
                          <div className="conf-pedido-progress-bar">
                            <div
                              className={`conf-pedido-progress-fill ${pedido.status}`}
                              style={{ width: `${pedido.total_volumes > 0 ? (pedido.quantidade_conferida / pedido.total_volumes) * 100 : 0}%` }}
                            ></div>
                          </div>

                          {/* Grid de volumes */}
                          <div className="conf-volumes-grid">
                            {Array.from({ length: pedido.total_volumes }, (_, i) => i + 1).map(vol => {
                              const conferido = pedido.volumes_conferidos.includes(vol);
                              return (
                                <div
                                  key={vol}
                                  className={`conf-volume-box ${conferido ? 'conferido' : 'faltante'}`}
                                  title={conferido ? `Volume ${vol} conferido` : `Volume ${vol} FALTANTE`}
                                >
                                  {vol}
                                </div>
                              );
                            })}
                          </div>

                          {/* Volumes faltantes */}
                          {pedido.volumes_faltantes.length > 0 && (
                            <div className="conf-volumes-faltantes">
                              <i className="fas fa-exclamation-triangle" style={{ color: '#e74c3c', marginRight: 4 }}></i>
                              <strong>Volumes faltantes:</strong> {pedido.volumes_faltantes.join(', ')}
                            </div>
                          )}

                          {/* Etiquetas bipadas (expandível) */}
                          {isExpanded && pedido.leituras.length > 0 && (
                            <div style={{
                              marginTop: 12,
                              borderTop: '1px solid #e5e7eb',
                              paddingTop: 12
                            }}>
                              <h5 style={{ fontSize: '0.85rem', color: '#374151', marginBottom: 8, fontWeight: 600 }}>
                                <i className="fas fa-barcode" style={{ marginRight: 6, color: '#6b7280' }}></i>
                                Etiquetas Bipadas
                              </h5>
                              <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ background: '#f9fafb' }}>
                                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Etiqueta</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: 70 }}>Volume</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: 110 }}>Status</th>
                                    <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: 140 }}>Data/Hora</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pedido.leituras.map((leitura, idx) => {
                                    const statusConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
                                      ok: { label: 'OK', color: '#16a34a', bg: '#f0fdf4', icon: 'check-circle' },
                                      duplicado: { label: 'Duplicada', color: '#d97706', bg: '#fffbeb', icon: 'copy' },
                                      nao_encontrado: { label: 'Divergente', color: '#dc2626', bg: '#fef2f2', icon: 'times-circle' },
                                      removido: { label: 'Removida', color: '#6b7280', bg: '#f3f4f6', icon: 'trash-alt' },
                                    };
                                    const st = statusConfig[leitura.resultado] || statusConfig.ok;
                                    return (
                                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '0.75rem', color: '#1f2937' }}>
                                          {leitura.codigo_lido}
                                        </td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center', color: '#374151' }}>
                                          {leitura.volume_atual}/{leitura.total_volumes}
                                        </td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                          <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                            padding: '2px 8px', borderRadius: 12,
                                            fontSize: '0.7rem', fontWeight: 600,
                                            color: st.color, background: st.bg
                                          }}>
                                            <i className={`fas fa-${st.icon}`}></i>
                                            {st.label}
                                          </span>
                                        </td>
                                        <td style={{ padding: '6px 10px', textAlign: 'right', color: '#6b7280', fontSize: '0.75rem' }}>
                                          {new Date(leitura.lido_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {!volumeLoading && !volumeError && !volumeResumo && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>
                  Clique na aba para carregar a análise de volumes.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Adicionar Item */}
      {showItemModal && (
        <div className="conferencia-modal-overlay">
          <div className="conferencia-modal">
            <div className="conferencia-modal-header">
              <h3>Adicionar Item</h3>
              <button className="close-btn" onClick={() => setShowItemModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="conferencia-modal-body">
              {modalError && <div className="conferencia-error">{modalError}</div>}
              <form onSubmit={handleAdicionarItem}>
                <div className="form-group"><label>Código de Barras</label><input type="text" className="form-control" value={itemForm.codigo_barras} onChange={e => setItemForm(f => ({ ...f, codigo_barras: e.target.value }))} placeholder="Ex: 7891234567890" /></div>
                <div className="form-group"><label>SKU</label><input type="text" className="form-control" value={itemForm.sku} onChange={e => setItemForm(f => ({ ...f, sku: e.target.value }))} placeholder="Código interno" /></div>
                <div className="form-group"><label>Descrição</label><input type="text" className="form-control" value={itemForm.descricao} onChange={e => setItemForm(f => ({ ...f, descricao: e.target.value }))} placeholder="Descrição do item" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label>Qtd. Esperada *</label><input type="number" className="form-control" min={1} value={itemForm.quantidade_esperada} onChange={e => setItemForm(f => ({ ...f, quantidade_esperada: parseInt(e.target.value) || 1 }))} required /></div>
                  <div className="form-group"><label>Unidade</label>
                    <select className="form-control" value={itemForm.unidade_medida} onChange={e => setItemForm(f => ({ ...f, unidade_medida: e.target.value }))}>
                      <option value="UN">UN</option><option value="CX">CX</option><option value="KG">KG</option><option value="PCT">PCT</option><option value="FD">FD</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} disabled={modalLoading}>{modalLoading ? 'Adicionando...' : 'Adicionar Item'}</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Documento */}
      {showDocModal && (
        <div className="conferencia-modal-overlay">
          <div className="conferencia-modal">
            <div className="conferencia-modal-header">
              <h3><i className="fas fa-file-alt" style={{ color: '#3498db', marginRight: 8 }}></i>{editingDocId ? 'Editar Documento' : 'Adicionar Documento'}</h3>
              <button className="close-btn" onClick={() => { setShowDocModal(false); setEditingDocId(null); }}><i className="fas fa-times"></i></button>
            </div>
            <div className="conferencia-modal-body">
              {modalError && <div className="conferencia-error">{modalError}</div>}
              <form onSubmit={handleSalvarDocumento}>
                <div className="form-group"><label>Tipo *</label>
                  <select className="form-control" value={docForm.tipo_documento} onChange={e => setDocForm(f => ({ ...f, tipo_documento: e.target.value }))} required>
                    <option value="nota_fiscal">Nota Fiscal</option><option value="manifesto">Manifesto</option><option value="romaneio">Romaneio</option><option value="cte">CT-e</option><option value="outro">Outro</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label>Número *</label><input type="text" className="form-control" value={docForm.numero_documento} onChange={e => setDocForm(f => ({ ...f, numero_documento: e.target.value }))} placeholder="Nº documento" required /></div>
                  <div className="form-group"><label>Série</label><input type="text" className="form-control" value={docForm.serie} onChange={e => setDocForm(f => ({ ...f, serie: e.target.value }))} placeholder="Série" /></div>
                </div>
                <div className="form-group"><label>Emitente</label><input type="text" className="form-control" value={docForm.emitente} onChange={e => setDocForm(f => ({ ...f, emitente: e.target.value }))} placeholder="Nome do emitente" /></div>
                <div className="form-group"><label>Destinatário</label><input type="text" className="form-control" value={docForm.destinatario} onChange={e => setDocForm(f => ({ ...f, destinatario: e.target.value }))} placeholder="Nome do destinatário" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label>Valor (R$)</label><input type="number" className="form-control" step="0.01" value={docForm.valor_total} onChange={e => setDocForm(f => ({ ...f, valor_total: e.target.value }))} placeholder="0,00" /></div>
                  <div className="form-group"><label>Peso (kg)</label><input type="number" className="form-control" step="0.01" value={docForm.peso_total} onChange={e => setDocForm(f => ({ ...f, peso_total: e.target.value }))} placeholder="0,00" /></div>
                  <div className="form-group"><label>Volumes</label><input type="number" className="form-control" min={1} value={docForm.total_volumes} onChange={e => setDocForm(f => ({ ...f, total_volumes: parseInt(e.target.value) || 1 }))} /></div>
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} disabled={modalLoading}>{modalLoading ? 'Salvando...' : (editingDocId ? 'Salvar Alterações' : 'Adicionar Documento')}</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Divergência */}
      {showDivModal && (
        <div className="conferencia-modal-overlay">
          <div className="conferencia-modal">
            <div className="conferencia-modal-header">
              <h3><i className="fas fa-exclamation-triangle" style={{ color: '#e74c3c', marginRight: 8 }}></i> Registrar Divergência</h3>
              <button className="close-btn" onClick={() => setShowDivModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="conferencia-modal-body">
              {modalError && <div className="conferencia-error">{modalError}</div>}
              <form onSubmit={handleCriarDivergencia}>
                <div className="form-group"><label>Tipo *</label>
                  <select className="form-control" value={divForm.tipo_divergencia} onChange={e => setDivForm(f => ({ ...f, tipo_divergencia: e.target.value }))} required>
                    <option value="falta">Falta</option><option value="sobra">Sobra</option><option value="avaria">Avaria</option><option value="troca">Troca</option><option value="inversao_rota">Inversão de Rota</option><option value="outro">Outro</option>
                  </select>
                </div>
                <div className="form-group"><label>Código de Barras</label><input type="text" className="form-control" value={divForm.codigo_barras} onChange={e => setDivForm(f => ({ ...f, codigo_barras: e.target.value }))} placeholder="Código do item" /></div>
                <div className="form-group"><label>Descrição</label><input type="text" className="form-control" value={divForm.descricao} onChange={e => setDivForm(f => ({ ...f, descricao: e.target.value }))} placeholder="Descreva a divergência" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label>Qtd. Esperada</label><input type="number" className="form-control" min={0} value={divForm.quantidade_esperada} onChange={e => setDivForm(f => ({ ...f, quantidade_esperada: e.target.value }))} placeholder="0" /></div>
                  <div className="form-group"><label>Qtd. Encontrada</label><input type="number" className="form-control" min={0} value={divForm.quantidade_encontrada} onChange={e => setDivForm(f => ({ ...f, quantidade_encontrada: e.target.value }))} placeholder="0" /></div>
                </div>
                <button type="submit" className="btn btn-danger" style={{ width: '100%', marginTop: 12 }} disabled={modalLoading}>{modalLoading ? 'Registrando...' : 'Registrar Divergência'}</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Resolver Divergência */}
      {showResolveModal && resolveTarget && (
        <div className="conferencia-modal-overlay">
          <div className="conferencia-modal">
            <div className="conferencia-modal-header">
              <h3>
                <i className={`fas fa-check-circle`} style={{ color: resolveTarget.status === 'resolvida' ? '#27ae60' : '#3498db', marginRight: 8 }}></i>
                {resolveTarget.status === 'resolvida' ? 'Resolver Divergência' : 'Aceitar Divergência'}
              </h3>
              <button className="close-btn" onClick={() => setShowResolveModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="conferencia-modal-body">
              <div className="form-group">
                <label>Descrição da resolução</label>
                <textarea
                  className="form-control"
                  value={resolveText}
                  onChange={e => setResolveText(e.target.value)}
                  rows={3}
                  placeholder={resolveTarget.status === 'resolvida' ? 'Descreva como a divergência foi resolvida...' : 'Descreva o motivo de aceitar esta divergência...'}
                  style={{ resize: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowResolveModal(false)}>Cancelar</button>
                <button
                  className={resolveTarget.status === 'resolvida' ? 'btn btn-success' : 'btn btn-primary'}
                  style={{ flex: 1 }}
                  onClick={async () => {
                    try {
                      await resolverDivergencia(resolveTarget.id, { status: resolveTarget.status, resolucao: resolveText || undefined });
                      setDivergencias(prev => prev.map(d => d.id === resolveTarget.id ? { ...d, status: resolveTarget.status, resolucao: resolveText || undefined } : d));
                      setShowResolveModal(false);
                    } catch (err) { console.error(err); }
                  }}
                >
                  {resolveTarget.status === 'resolvida' ? 'Resolver' : 'Aceitar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenciaView;
